import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import {InputSwitch} from 'primereact/inputswitch'
import {MultiSelect} from 'primereact/multiselect'
import React, {forwardRef, useEffect, useImperativeHandle, useMemo} from 'react'
import {v4 as uuidv4} from 'uuid'
import {HTTP_STATUS_OK} from '../../Constants'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId} from '../authentication/AuthUtils'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {
    useListPathways,
    usePathwayAssignment,
} from '../pathway/query-keys/PathwayKeysManager'
import {UserPropertyType} from '../user-property/UserPropertyConstants'
import {useListUserPathways} from '../user-property/query-keys/UserPropertyQueryManager'
import {useDatatableContext} from './DataTableContext'
import {
    assignmentEvent,
    assignmentEventTypes,
    getAssignmentEventKey,
    removeToggleAssignmentFormListener,
} from './broadcast/AssignmentEvent'
import {
    getPathwayEventKey,
    onPathwayTableChangeRemoveListener,
    pathwayEvent,
    pathwayEventTypes,
} from './pathway/broadcast/PathwayEvent'
import {
    getPatientEventKey,
    onPatientPathwayChangeRemoveListener,
    patientEvent,
    patientEventTypes,
} from './patient/broastcast/PatientEvent'

const InitialPathwayFilterRequest = {}
const InitialOwnedPathwayFilterRequest = {}

const StyledAssignmentPathway = forwardRef(({userIds = []}, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))
    //#region Context
    const {user} = useAuth()
    const {currentHospital} = useUserHospital()
    const {tableType} = useDatatableContext()
    const queryClient = useQueryClient()
    //#endregion

    //#region Translate
    const {translateCommons, translateKeys, translateOptions} = TranslateUtils()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const panelVisible = useSignal(false)
    const selectedItems = useSignal([])
    const override = useSignal(false)

    const handleChangeSelectedItems = e => {
        selectedItems.value = e.value
    }
    //#endregion

    //#region Query
    const {data: pathwayOptions, isFetched} = useListPathways(
        user?.id,
        true,
        InitialPathwayFilterRequest,
        !!user?.id,
    )

    const {data: ownedPathways, refetch} = useListUserPathways(
        user?.id,
        false,
        {
            ...InitialOwnedPathwayFilterRequest,
            userId: !!userIds && userIds?.length === 1 ? userIds[0] : null,
        },
        !!user?.id && !!userIds && userIds?.length === 1,
    )

    const {mutateAsync: assignPatientToPathway} = usePathwayAssignment(user?.id)
    //#endregion

    //#region Memo
    const ownPathwayIds = useMemo(() => {
        if (!!userIds && userIds?.length > 1) {
            return []
        }
        return ownedPathways?.map(x => x.pathwayId)
    }, [ownedPathways, userIds])
    //#endregion

    //#region Handle Function
    const handleShowForm = () => {
        blockOverflownScroll()
        visible.value = true

        if (ownPathwayIds?.length > 0 && userIds?.length === 1) {
            selectedItems.value = [...ownPathwayIds]
        }
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        selectedItems.value = []
        override.value = false
        visible.value = false
    }

    const handleOnSubmit = async () => {
        if (!userIds || userIds?.length < 1) {
            showNotification(
                severityNotification.warn,
                'Please select the patients whom you want to assign Pathway to !',
            )

            return
        }

        if (!override.value && selectedItems.value?.length < 1) {
            showNotification(
                severityNotification.warn,
                'Please select the Pathway you want to assign to the selected patients !',
            )

            return
        }

        let toPostData = {
            userIds: userIds,
            pathwayIds: selectedItems.value,
            override: userIds?.length > 1 ? override.value : true,
        }

        const responseStatus = await assignPatientToPathway(toPostData)

        if (responseStatus && responseStatus === HTTP_STATUS_OK) {
            const {patientKeys} = QueryKeyManager(
                QueryKeyTypes.patient,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: patientKeys.pattern(),
            })

            if (userIds?.length === 1) {
                refetch()
            }

            handleHideForm()
        }
    }
    //#endregion

    //#region Render & Template
    const panelVisibilityHandler = () => ({
        optionLabel: 'label',
        optionValue: 'value',
        emptyFilterMessage: translateCommons('No results found'),
        resetFilterOnHide: true,
        filter: true,
        filterBy: 'label',
        filterPlaceholder: translateCommons('Search here'),
        showClear: true,
        onShow: () => (panelVisible.value = true),
        onHide: () => (panelVisible.value = false),
    })

    const panelVisiblilityProps = panelVisibilityHandler()

    const renderPanelFooterTemplate = selectedData => {
        let shouldRenderItemSelected = false
        let itemSelectedLength = 0

        if (!!selectedData && typeof selectedData === 'object') {
            if (isArrayAndNotEmpty(selectedData)) {
                shouldRenderItemSelected = true

                itemSelectedLength = selectedData?.length
            }

            if (
                !shouldRenderItemSelected &&
                Object.keys(selectedData)?.length > 0
            ) {
                shouldRenderItemSelected = true

                itemSelectedLength = Object.keys(selectedData)?.length
            }
        }

        return (
            <div className="p-3" style={{backgroundColor: '#f9f9f9'}}>
                <b>
                    {shouldRenderItemSelected && itemSelectedLength > 0
                        ? `${itemSelectedLength} ${
                              itemSelectedLength > 1 ? 'items' : 'item'
                          } selected`
                        : `No item selected`}
                </b>
            </div>
        )
    }
    //#endregion

    //#region Event Lister for Toggle
    useEffect(() => {
        const {pathwayKeys} = QueryKeyManager(QueryKeyTypes.pathway, user?.id)
        const {userPropertyKeys} = QueryKeyManager(
            QueryKeyTypes.userProperty,
            user?.id,
        )
        // TABLE CHANGE
        pathwayEvent.on(
            `${getPathwayEventKey(pathwayEventTypes.TABLE_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                queryClient.invalidateQueries({
                    queryKey: pathwayKeys.pattern(),
                })
                queryClient.invalidateQueries({
                    queryKey: userPropertyKeys.pattern({
                        type: UserPropertyType.userPathway,
                    }),
                })
            },
        )

        // PATIENT PATHWAY CHANGE
        patientEvent.on(
            `${getPatientEventKey(patientEventTypes.PATHWAY_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    !data?.userIds ||
                    !data?.userIds?.length ||
                    !data?.userIds?.some(userId => userIds.includes(userId))
                ) {
                    return
                }

                queryClient.invalidateQueries({
                    queryKey: pathwayKeys.pattern(),
                })

                queryClient.invalidateQueries({
                    queryKey: userPropertyKeys.pattern({
                        type: UserPropertyType.userPathway,
                    }),
                })
            },
        )

        assignmentEvent.on(
            `${getAssignmentEventKey(
                tableType,
                assignmentEventTypes.ON_TOGGLE_PATHWAY,
            )}`,
            () => {
                handleShowForm()
            },
        )

        return () => {
            removeToggleAssignmentFormListener(
                tableType,
                assignmentEventTypes.ON_TOGGLE_PATHWAY,
            )

            onPatientPathwayChangeRemoveListener()
            onPathwayTableChangeRemoveListener()
        }
    }, [])
    //#endregion

    return (
        <Dialog
            header={
                <h5 className="mb-0 font-bold">
                    <strong>
                        {translateCommons('Assign Patients to Pathway')}
                    </strong>
                </h5>
            }
            visible={visible.value}
            onHide={handleHideForm}
            blockScroll={true}
            style={{width: '50vw'}}
            breakpoints={{'960px': '75vw', '641px': '100vw'}}>
            <div className="w-full">
                <div className="mb-2">
                    <h4 className="font-bold mb-1  underline-60">
                        {translateKeys('Pathway')}
                    </h4>
                </div>
                <p>
                    {translateCommons(
                        'Select pathways to which you want to assign the selected patients',
                    )}
                    {` `}
                    <span className="font-bold">
                        ({userIds?.length} patient(s))
                    </span>
                </p>

                <div className="col-12 mb-3">
                    {userIds?.length > 1 ? (
                        <div className="flex align-items-center justify-content-between py-2 mb-3">
                            <div>
                                <h6 className="font-bold">
                                    Override Existing Assignment
                                </h6>
                                <p>
                                    {translateCommons(
                                        'This option enables you whether to override the existing assignment or not',
                                    )}
                                </p>
                                <p>
                                    <i className="pi pi-info-circle mr-2"></i>
                                    <span className="font-bold">
                                        {override.value ? (
                                            <span>
                                                With override option enabled,{' '}
                                                <span style={{color: 'red'}}>
                                                    any existing assignment will
                                                    be deleted
                                                </span>{' '}
                                                and the selected patients will
                                                be assigned to the selected
                                                Pathway(s) regardless of their
                                                existing assignment.
                                            </span>
                                        ) : (
                                            <span>
                                                With override option disabled,{' '}
                                                <span className="text-darkcyan">
                                                    any existing assignment will
                                                    be kept
                                                </span>{' '}
                                                and the selected patients will
                                                be assigned to the selected
                                                Pathway(s) only if they are not
                                                already assigned to them.
                                            </span>
                                        )}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <InputSwitch
                                    checked={override.value}
                                    onChange={() => {
                                        override.value = !override.value
                                    }}
                                />
                            </div>
                        </div>
                    ) : null}

                    <div className="flex align-items-center justify-content-between py-2">
                        <h6 className="font-bold">
                            {translateCommons('Existing pathways')}
                        </h6>
                    </div>
                    <MultiSelect
                        className="w-full h-3rem p-inputtext p-0 font-bold"
                        options={
                            isArrayAndNotEmpty(pathwayOptions)
                                ? translateOptions(pathwayOptions)
                                : []
                        }
                        placeholder={translateCommons('Select an option')}
                        defaultValue={ownedPathways || []}
                        maxSelectedLabels={3}
                        display={
                            selectedItems.value?.length < 4 ? 'chip' : 'comma'
                        }
                        value={selectedItems.value || []}
                        onChange={handleChangeSelectedItems}
                        itemTemplate={item => {
                            return (
                                <div className="flex align-items-center">
                                    <div className="mx-2">
                                        <img
                                            src={item.data}
                                            style={{
                                                height: '25px',
                                                width: '25px',
                                            }}
                                            alt={`${item.label}`}
                                        />
                                    </div>
                                    <div>{item.label}</div>
                                </div>
                            )
                        }}
                        panelFooterTemplate={() =>
                            renderPanelFooterTemplate(selectedItems.value)
                        }
                        {...panelVisiblilityProps}
                    />
                    <p className="font-bold">
                        You have selected{' '}
                        <span className="text-darkcyan">
                            {selectedItems.value?.length < 1
                                ? 'no'
                                : selectedItems.value?.length}{' '}
                            pathway(s)
                        </span>{' '}
                        out of{' '}
                        <span className="text-darkcyan">
                            {pathwayOptions?.length} available pathway(s)
                        </span>
                    </p>

                    <Divider align="center" layout="horizontal" />
                    <h6 className="font-bold mb-2">
                        {translateCommons('Note')}
                    </h6>
                    <ul>
                        <li className="mb-2">
                            You can assign the selected patients to multiple
                            pathways.
                        </li>
                        <li className="mb-2">
                            If you want to remove the patient from the pathway,
                            please select only that patient and change the
                            pathway assignment.
                        </li>
                    </ul>

                    {selectedItems.value?.length > 0 && (
                        <React.Fragment>
                            <Divider align="center" layout="horizontal" />
                            <h6 className="font-bold">
                                {translateCommons('Summary')}
                            </h6>
                            <p>
                                {' '}
                                You are about to assign the selected patients to
                                the following pathways:
                            </p>
                            <ul>
                                {selectedItems.value?.length > 0 ? (
                                    <React.Fragment>
                                        {selectedItems.value?.map(
                                            (selectedPathway, index) => {
                                                if (index > 3) {
                                                    return null
                                                }

                                                const pathway =
                                                    pathwayOptions?.find(
                                                        x =>
                                                            x.value ===
                                                            selectedPathway,
                                                    )

                                                if (!pathway) {
                                                    return null
                                                }

                                                return (
                                                    <li
                                                        key={`${uuidv4()}`}
                                                        className="mb-2">
                                                        <div className="flex align-items-center">
                                                            <div className="mx-2">
                                                                <img
                                                                    src={
                                                                        pathway.data
                                                                    }
                                                                    style={{
                                                                        height: '25px',
                                                                        width: '25px',
                                                                    }}
                                                                    alt={`${pathway.label}`}
                                                                />
                                                            </div>
                                                            <div>
                                                                {pathway.label}
                                                            </div>
                                                        </div>
                                                    </li>
                                                )
                                            },
                                        )}
                                        {selectedItems.value?.length > 4 ? (
                                            <li className="mb-2 py-2">
                                                <div className="flex align-items-center">
                                                    <div className="font-bold">
                                                        {`+ ${
                                                            selectedItems.value
                                                                .length - 4
                                                        } more pathway(s)`}
                                                    </div>
                                                </div>
                                            </li>
                                        ) : null}
                                    </React.Fragment>
                                ) : null}
                            </ul>
                        </React.Fragment>
                    )}
                </div>

                <Button
                    label={translateCommons('SUBMIT')}
                    icon="pi pi-send"
                    onClick={handleOnSubmit}
                    autoFocus
                    className="col-12 h-3rem mb-3"
                    disabled={
                        selectedItems.value?.length < 1 && !override.value
                    }
                />
            </div>
        </Dialog>
    )
})

export default StyledAssignmentPathway
